import { FlexLayout, Text } from '@deity/falcon-ui';
import type { IconType } from '@deity-io/portal-ui';
import { Button, Icon } from '@deity-io/portal-ui';
import { ConfirmationModal } from 'components';
import { DeployPackageVersionModal } from 'components/PackageVersion/DeployPackageVersionModal';
import { StatusBadge } from 'components/StatusBadge';
import { ENTITY_PATH, ENTITY_PATH_ID, LOCALE_DATE_OPTIONS, PACKAGE_VERSION_PATH } from 'contracts/constants';
import type { PopoverActionMenuOption } from 'contracts/interfaces/PopoverActionMenuOption';
import type { FC } from 'react';
import { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import {
  GetAllPackageVersionsDocument,
  GetPackageVersionDocument,
  useUndeployPackageVersionMutation
} from 'services/graphql/generated/hooks';
import { ContentStatus } from 'services/graphql/generated/schemas';
import { getParams } from 'utils';
import { isPackageEditable } from 'utils/is-package-editable';
import ActionPopover from './ActionPopover';

type PackageVersionItemProps = {
  packageUuid: string;
  uuid: string;
  index: number;
  version: string;
  name: string | undefined;
  status: ContentStatus;
  modifiedAt: string;
  modifiedBy: string;
};
export const PackageVersionItem: FC<PackageVersionItemProps> = ({
  packageUuid,
  uuid,
  index,
  name,
  modifiedAt,
  modifiedBy,
  version,
  status
}) => {
  const history = useHistory();
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isUndeployModalOpen, seIsUndeployModalOpen] = useState(false);
  const { pathname } = useLocation();
  const params = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH, PACKAGE_VERSION_PATH]);
  const [undeployPackageVersion] = useUndeployPackageVersionMutation({
    refetchQueries: [
      {
        query: GetPackageVersionDocument,
        variables: {
          uuid
        }
      },
      {
        query: GetAllPackageVersionsDocument,
        variables: {
          packageUuid: params.packageId,
          limit: 20,
          offset: 0
        }
      }
    ]
  });

  const defaultActionBtn: { text: string; icon: IconType } = isPackageEditable(status)
    ? { text: 'Edit', icon: 'edit' }
    : { text: 'View', icon: 'visibility' };

  const actionMenuOptions: PopoverActionMenuOption<IconType, string, string>[] = [
    { ...defaultActionBtn, action: 'view' },
    { text: 'Copy', icon: 'contentCopy', action: 'copy' },
    { text: 'Delete', icon: 'delete', action: 'delete' }
  ];

  const onActionSelect = (action: string) => {
    const actions = {
      view: () => history.push(`/packages/${packageUuid}/${uuid}/package`),
      copy: () => {},
      delete: () => {}
    };
    actions[action]();
  };

  // eslint-disable-next-line react/no-unstable-nested-components
  const DeployedColumn = () => {
    if (status === ContentStatus.Published) {
      return (
        <Button endIcon={<Icon icon="cloudUpload" />} onClick={() => setIsDeployModalOpen(true)}>
          Deploy
        </Button>
      );
    }
    if (status === ContentStatus.Deployed) {
      return (
        <Button endIcon={<Icon icon="cloudUpload" />} onClick={() => seIsUndeployModalOpen(true)}>
          Undeploy
        </Button>
      );
    }

    return null;
  };

  return (
    <>
      <Text color="primaryText">{index}</Text>

      <Text fontSize="md" color="primaryText" fontWeight="bold">
        {name}
      </Text>

      <Text fontSize="md" color="primaryText" style={{ justifySelf: 'center' }}>
        {version}
      </Text>

      <Text fontSize="md" color="primaryText">
        {modifiedAt ? new Date(modifiedAt).toLocaleString('nl', LOCALE_DATE_OPTIONS) : ''}
      </Text>

      {/* TODO: Remove hardcoded value with DI-491 */}
      <Text fontSize="md" color="primaryText">
        John Smith
      </Text>

      <StatusBadge status={status} />

      <FlexLayout alignItems="center" justifyContent="end" css={{ width: '100%' }}>
        <DeployedColumn />
      </FlexLayout>

      <FlexLayout alignItems="center" justifyContent="end" css={{ width: '100%' }}>
        <ActionPopover
          defaultAction="view"
          defaultActionTitle={defaultActionBtn.text}
          options={actionMenuOptions}
          onSelect={onActionSelect}
        />
      </FlexLayout>

      <DeployPackageVersionModal
        packageId={packageUuid}
        versionUuid={uuid}
        isOpen={isDeployModalOpen}
        setIsOpen={setIsDeployModalOpen}
      />
      <ConfirmationModal
        isOpen={isUndeployModalOpen}
        setIsOpen={seIsUndeployModalOpen}
        entityType="package version"
        entityUuid={uuid}
        actionType="undeploy"
        confirmText="Undeploy"
        onConfirm={versionUuid => versionUuid && undeployPackageVersion({ variables: { uuid: versionUuid } })}
      />
    </>
  );
};
